import React, { Suspense, useCallback, useMemo } from 'react';
import { useLazyLoadQuery, useRelayEnvironment } from 'react-relay/hooks';

import { Paper, Toolbar } from '@material-ui/core';
import { graphql } from 'babel-plugin-relay/macro';
import { ErrorBoundary } from 'core';
import { concat, defaultsDeep, filter, find, forEach, isEmpty, map, times, uniq } from 'lodash';
import ConnectionHandler from 'relay-connection-handler-plus';
import { commitLocalUpdate, fetchQuery } from 'relay-runtime';
import { LoggerStatusEnum, sendLog } from 'ui-model';

import { ListChatActivities, listChatActivitiesConnectionKey, ListChatActivitiesProps } from 'deprecated/fragments';

import { ContentLoaderListItemChatActivity } from 'components/v2';
import useSocketEventsDebounced from 'hooks/useSocketEventsDebounced';
import { toGlobalId } from 'services/relayService';

import {
  QueryListChatActivitiesQuery,
  QueryListChatActivitiesQueryVariables,
} from './__generated__/QueryListChatActivitiesQuery.graphql';
import { QueryListChatActivitiesRefreshQuery } from './__generated__/QueryListChatActivitiesRefreshQuery.graphql';

const socketEvents = ['chat:server:chatActivity'];

const queryRefresh = graphql`
  query QueryListChatActivitiesRefreshQuery(
    $includeChatActivity: Boolean = true
    $includeAssignee: Boolean = true
    $filterCount: FilterChatActivityInput!
    $filter: FilterFindManyChatActivityInput!
  ) {
    # To update counts inside the Relay store (tab count)
    ...TypographyChatActivityCount_Query @arguments(filter: $filterCount)

    # To update ChatActivity data inside the Relay store
    chatActivityMany(filter: $filter) {
      id
      _id
      deleted
      readByUser
      oid_patient
      oid_assignee
      oid_groupAssignee
      ...ListItemChatActivity_ChatActivity @arguments(includeAssignee: $includeAssignee)
      # WORKAROUND for bulk actions and actions in the right panel
      patient {
        _id
        ...ActionsPatient_Patient @arguments(includeChatActivity: $includeChatActivity)
      }

      # WORKAROUND to refresh fields in right panel with no need to fetch Patient.chatActivity lookup
      # TODO remove once BE fixes dataloader/model for Patient.chatActivity
      status
      assignee {
        _id
      }
    }
  }
`;

export interface QueryListChatActivitiesProps extends Omit<ListChatActivitiesProps, 'classes' | 'fragmentRef'> {
  variables: QueryListChatActivitiesQueryVariables;
}

/**
 * TODO: Split into `QueryQueryListChatActivities` and `FragmentQueryListChatActivities` + cleanup
 *
 * @param props
 */
const QueryListChatActivities: React.FC<QueryListChatActivitiesProps> = ({
  variables: variablesProp,
  patients,
  ...rest
}) => {
  const variables = useMemo(() => ({ ...variablesProp, filterCount: variablesProp.filter }), [variablesProp]);

  const data = useLazyLoadQuery<QueryListChatActivitiesQuery>(
    graphql`
      query QueryListChatActivitiesQuery(
        $filterCount: FilterChatActivityInput!
        $filter: FilterFindManyChatActivityInput!
        $includeAssignee: Boolean!
      ) {
        ...ListChatActivities_Query
          @arguments(filter: $filter, filterCount: $filterCount, includeAssignee: $includeAssignee)
      }
    `,
    variables,
  );

  const environment = useRelayEnvironment();

  const handleSocketEvents = useCallback(
    async (values: any[]) => {
      let isSubscribed = true;
      // Ignore read/unread events
      const array = filter(values, (item) => item.action !== 'read' && item.action !== 'unread');
      const ids = uniq(concat(map(array, '_id')));
      if (isEmpty(ids)) {
        return;
      }
      const handleChatActivity = (chatActivityId: string, deleted?: boolean) => {
        commitLocalUpdate(environment, (store) => {
          const connectionRecord = ConnectionHandler.getConnection(store.getRoot(), listChatActivitiesConnectionKey, {
            filter: variables.filter,
          });
          if (!connectionRecord) {
            sendLog('Connection not found', { key: listChatActivitiesConnectionKey }, LoggerStatusEnum.warn);
            return;
          }
          const globalId = toGlobalId('ChatActivity', chatActivityId);
          ConnectionHandler.deleteNode(connectionRecord, globalId);
          if (deleted) {
            return;
          }
          const chatActivityRecord = store.get(globalId);
          if (!chatActivityRecord) {
            sendLog('Record not found', { globalId }, LoggerStatusEnum.warn);
            return;
          }
          const newEdge = ConnectionHandler.createEdge(store, connectionRecord, chatActivityRecord, 'ChatActivityEdge');
          if (newEdge) {
            try {
              ConnectionHandler.insertEdgeBefore(connectionRecord, newEdge);
            } catch (error) {
              sendLog('Cannot insertEdgeBefore QueryListChatActivities', error, LoggerStatusEnum.error);
            }
          }
        });
      };

      const response = await fetchQuery<QueryListChatActivitiesRefreshQuery>(environment, queryRefresh, {
        filterCount: variables.filter,
        filter: defaultsDeep({}, { _operators: { _id: { in: ids } } }, variables.filter),
        includeAssignee: true,
        // WORKAROUND skip fetching Patient.chatActivity lookup
        // TODO remove once BE fixes dataloader/model for Patient.chatActivity
        includeChatActivity: false,
      });
      if (isSubscribed) {
        forEach(ids, (id) => {
          const chatActivity = find(response.chatActivityMany, { _id: id });
          const deleted = !chatActivity || chatActivity.deleted !== 0;
          handleChatActivity(id, deleted);
        });
      }
      // Cancel the promise
      // https://juliangaramendy.dev/blog/use-promise-subscription
      return () => {
        isSubscribed = false;
      };
    },
    [environment, variables.filter],
  );

  useSocketEventsDebounced(socketEvents, handleSocketEvents);

  return <ListChatActivities fragmentRef={data} patients={patients} {...rest} />;
};

export default (props: QueryListChatActivitiesProps) => (
  <ErrorBoundary>
    <Suspense
      fallback={
        <>
          <Paper square>
            <Toolbar />
          </Paper>
          {times(props.variant !== 'popper' ? 15 : 5, (i) => (
            <ContentLoaderListItemChatActivity key={i} />
          ))}
        </>
      }
    >
      <QueryListChatActivities {...props} />
    </Suspense>
  </ErrorBoundary>
);
